import { parse_float_or_return_null } from '../helpers';
import { is_sensitive_text_surface, scrub_url_string } from './privacy';

const tracked_attribute_names = [
	'data-hub-event-name',
	'data-hub-event-sub-type',
	'data-hub-value',
	'data-hub-form-element',
	'data-hub-form-id',
	'data-hub-form-name',
	'data-hub-field-name'
];

function escape_css_identifier(identifier) {
	try {
		if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
			return CSS.escape(identifier);
		}
		return String(identifier).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
	} catch {
		return null;
	}
}

function read_attribute(element, attribute_name) {
	return element?.getAttribute?.(attribute_name) || null;
}

function truncate_text(text_value, maximum_length = 300) {
	if (!text_value || typeof text_value !== 'string') {
		return null;
	}
	const normalized_text = text_value.replace(/\s+/g, ' ').trim();
	if (!normalized_text) {
		return null;
	}
	return normalized_text.slice(0, maximum_length);
}

const non_visible_descendant_tag_names = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'SVG']);

/**
 * Text for click analytics should reflect what the user sees, not raw DOM text nodes.
 * textContent on <body> (or a wrapper) includes inline <script> source — innerText does not.
 */
function read_element_text_for_click_analytics(element) {
	if (!element || element.nodeType !== Node.ELEMENT_NODE || is_sensitive_text_surface(element)) {
		return null;
	}
	try {
		if (typeof element.innerText === 'string') {
			return element.innerText;
		}
	} catch {
		// Some node types throw when reading innerText; fall back below.
	}
	if (typeof document === 'undefined' || typeof document.createTreeWalker !== 'function') {
		return element.textContent;
	}
	const text_parts = [];
	const text_node_walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
		acceptNode(text_node) {
			let ancestor_element = text_node.parentElement;
			while (ancestor_element && ancestor_element !== element) {
				if (non_visible_descendant_tag_names.has(ancestor_element.tagName)) {
					return NodeFilter.FILTER_REJECT;
				}
				ancestor_element = ancestor_element.parentElement;
			}
			return NodeFilter.FILTER_ACCEPT;
		}
	});
	while (text_node_walker.nextNode()) {
		const text_node_value = text_node_walker.currentNode?.nodeValue;
		if (text_node_value) {
			text_parts.push(text_node_value);
		}
	}
	return text_parts.join('');
}

function get_nth_child(element) {
	if (!element?.parentElement) {
		return null;
	}
	return Array.from(element.parentElement.children).indexOf(element) + 1;
}

function get_nth_of_type(element) {
	if (!element?.parentElement) {
		return null;
	}
	const tag_name = element.tagName;
	return Array.from(element.parentElement.children)
		.filter((sibling) => sibling.tagName === tag_name)
		.indexOf(element) + 1;
}

function build_short_selector(element) {
	if (!element || element.nodeType !== Node.ELEMENT_NODE) {
		return null;
	}
	const tag_name = element.tagName.toLowerCase();
	const escaped_id = element.id ? escape_css_identifier(element.id) : null;
	const id = escaped_id ? `#${escaped_id}` : '';
	if (id) {
		return `${tag_name}${id}`;
	}
	const class_selector = element.className && typeof element.className === 'string'
		? element.className.split(/\s+/).filter(Boolean).slice(0, 3).map((class_name) => {
			const escaped_class_name = escape_css_identifier(class_name);
			return escaped_class_name ? `.${escaped_class_name}` : '';
		}).join('')
		: '';
	return `${tag_name}${class_selector}:nth-child(${get_nth_child(element) || 1})`;
}

function build_attribute_payload(element) {
	return tracked_attribute_names.reduce((attribute_payload, attribute_name) => {
		const attribute_value = read_attribute(element, attribute_name);
		if (attribute_value !== null) {
			attribute_payload[attribute_name] = attribute_value;
		}
		return attribute_payload;
	}, {});
}

function describe_element_for_chain(element) {
	return {
		tag_name: element.tagName.toLowerCase(),
		text: truncate_text(read_element_text_for_click_analytics(element), 120),
		attr_id: element.id || null,
		attr_class: read_attribute(element, 'class'),
		attr_role: read_attribute(element, 'role'),
		attr_href: scrub_url_string(read_attribute(element, 'href')),
		nth_child: get_nth_child(element),
		nth_of_type: get_nth_of_type(element)
	};
}

/**
 * Builds the ancestor chain for a click target. Prefers `dom_event.composedPath()`
 * when available so the walk crosses shadow root boundaries (target → ... → shadow
 * host → host's light-DOM parents). Falls back to `parentElement`, which stops at
 * shadow roots — fine for synthetic test events that don't expose composedPath.
 */
function build_elements_chain(element, dom_event = null) {
	const composed_path = typeof dom_event?.composedPath === 'function'
		? dom_event.composedPath()
		: null;
	if (Array.isArray(composed_path) && composed_path.length > 0) {
		let composed_elements = composed_path.filter(
			(node) => node && node.nodeType === Node.ELEMENT_NODE
		);
		const target_index = composed_elements.indexOf(element);
		if (target_index > 0) {
			composed_elements = composed_elements.slice(target_index);
		} else if (target_index < 0 && element) {
			composed_elements = [element, ...composed_elements];
		}
		return composed_elements.slice(0, 8).map(describe_element_for_chain);
	}
	const chain = [];
	let current_element = element;
	while (current_element && current_element.nodeType === Node.ELEMENT_NODE && chain.length < 8) {
		chain.push(describe_element_for_chain(current_element));
		current_element = current_element.parentElement;
	}
	return chain;
}

/**
 * Returns the innermost element the user actually clicked, peering through any
 * open shadow roots via `composedPath()`. The browser retargets `event.target`
 * to the shadow host on listeners attached outside the shadow tree, so reading
 * `event.target` alone would collapse a click on (say) a label inside our
 * <data-hub-signup-form> down to the host element itself.
 */
export function resolve_composed_event_target_element(dom_event) {
	if (typeof dom_event?.composedPath === 'function') {
		const composed_path = dom_event.composedPath();
		if (Array.isArray(composed_path)) {
			for (const node of composed_path) {
				if (node && node.nodeType === Node.ELEMENT_NODE) {
					return node;
				}
			}
		}
	}
	const fallback_target = dom_event?.target;
	return fallback_target && fallback_target.nodeType === Node.ELEMENT_NODE
		? fallback_target
		: null;
}

/**
 * True when the click target is the page shell (body/html), not a meaningful control.
 * Auto $click capture skips these — they are usually padding misses, not CTAs.
 */
export function should_skip_auto_click_capture_for_target(element) {
	if (!element || element.nodeType !== Node.ELEMENT_NODE) {
		return true;
	}
	if (typeof document !== 'undefined') {
		if (element === document.body || element === document.documentElement) {
			return true;
		}
	}
	const tag_name = element.tagName?.toLowerCase();
	return tag_name === 'body' || tag_name === 'html';
}

export function build_element_context_from_click_event(dom_event, resolved_target_element = null) {
	const target_element = resolved_target_element
		&& resolved_target_element.nodeType === Node.ELEMENT_NODE
		? resolved_target_element
		: resolve_composed_event_target_element(dom_event);
	if (!target_element || target_element.nodeType !== Node.ELEMENT_NODE) {
		return null;
	}
	const bounding_box = target_element.getBoundingClientRect?.() || {};
	const href = read_attribute(target_element, 'href');
	let href_url = null;
	try {
		href_url = href ? new URL(href, window.location.href) : null;
	} catch {
		href_url = null;
	}
	return {
		text: truncate_text(read_element_text_for_click_analytics(target_element)),
		tag_name: target_element.tagName.toLowerCase(),
		attr_id: target_element.id || null,
		attr_class: read_attribute(target_element, 'class'),
		attr_role: read_attribute(target_element, 'role'),
		attr_href: scrub_url_string(href),
		attr_name: read_attribute(target_element, 'name'),
		attr_type: read_attribute(target_element, 'type'),
		attr_title: read_attribute(target_element, 'title'),
		attr_aria_label: read_attribute(target_element, 'aria-label'),
		href_domain: href_url?.host || null,
		href_pathname: href_url?.pathname || null,
		is_same_origin_href: href_url ? href_url.origin === window.location.origin : null,
		selector: build_short_selector(target_element),
		position_x: Math.round(dom_event.clientX || 0),
		position_y: Math.round(dom_event.clientY || 0),
		page_x: Math.round(dom_event.pageX || dom_event.clientX || 0),
		page_y: Math.round(dom_event.pageY || dom_event.clientY || 0),
		bbox_left: Math.round(bounding_box.left || 0),
		bbox_top: Math.round(bounding_box.top || 0),
		bbox_right: Math.round(bounding_box.right || 0),
		bbox_bottom: Math.round(bounding_box.bottom || 0),
		bbox_width: Math.round(bounding_box.width || 0),
		bbox_height: Math.round(bounding_box.height || 0),
		scroll_x: Math.round(window.scrollX || 0),
		scroll_y: Math.round(window.scrollY || 0),
		nth_child: get_nth_child(target_element),
		nth_of_type: get_nth_of_type(target_element),
		dom_event: dom_event.type || 'click',
		click_button: dom_event.button ?? null,
		click_count: dom_event.detail ?? null,
		data_hub_value: parse_float_or_return_null(read_attribute(target_element, 'data-hub-value')),
		data_hub_form_id: read_attribute(target_element, 'data-hub-form-id'),
		data_hub_form_name: read_attribute(target_element, 'data-hub-form-name'),
		data_hub_field_name: read_attribute(target_element, 'data-hub-field-name'),
		is_data_hub_form_element: read_attribute(target_element, 'data-hub-form-element') === 'true',
		is_outbound_link: href_url ? href_url.origin !== window.location.origin : false,
		is_download_link: Boolean(read_attribute(target_element, 'download')),
		closest_heading_text: truncate_text(target_element.closest('section, article, main, div')?.querySelector?.('h1,h2,h3,h4,h5,h6')?.textContent, 200),
		closest_section_selector: build_short_selector(target_element.closest('section, article, main, div')),
		attributes: build_attribute_payload(target_element),
		elements_chain: build_elements_chain(target_element, dom_event)
	};
}
